import React, { MouseEvent, useContext, useEffect, useState } from 'react';
import { customerDisplayName } from '@src/helpers/CustomerDisplay';
import CustomerLanguage from './CustomerLanguage';
import {
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Skeleton,
	Tooltip,
} from '@mui/material';
import { ArrowBack, MoreVert, Search } from '@mui/icons-material';
import TeamLockBadge from '@src/components/TeamLockBadge';
import { EVENT_TOPIC_FORCE_REFRESH_CHAT } from '@src/Constants';
import PubSub from 'pubsub-js';
import { extractAvatarFromContactProviderData } from '@src/helpers/Helpers';
import { addPlus } from '@src/helpers/PhoneNumberHelper';
import WarningIcon from '@mui/icons-material/Warning';
import { isMobileOnly } from 'react-device-detect';
import {
	getDisplayAssignmentAndTaggingHistory,
	setDisplayAssignmentAndTaggingHistory,
} from '@src/helpers/StorageHelper';
import { useTranslation } from 'react-i18next';
import PrintMessage from '../PrintMessage';
import AssigneeChip from '@src/components/AssigneeChip';
import useChatAssignmentAPI from '@src/hooks/api/useChatAssignmentAPI';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SellIcon from '@mui/icons-material/Sell';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CloseIcon from '@mui/icons-material/Close';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { setState } from '@src/store/reducers/UIReducer';
import { Chat } from '@src/types/chats';
import * as Styled from './ChatHeader.styles';
import { Person } from '@src/types/persons';
import { isPersonExpired } from '@src/helpers/PersonHelper';
import TagsChip from '@src/components/TagsChip';
import { AxiosError } from 'axios';
import { updateResolved } from '@src/api/chatsApi';
import DoneIcon from '@mui/icons-material/Done';
import { AppConfigContext } from '@src/contexts/AppConfigContext';
import { useIsUserActionsRestricted } from '@src/hooks/useIsUserActionsRestricted';

interface Props {
	chat?: Chat;
	person?: Person;
	contactProvidersData: { [key: string]: any };
	isChatOnly: boolean | Number;
	setChatTagsVisible: (isVisible: boolean) => void;
	closeChat: () => void;
	waId: string;
}

const ChatHeader: React.FC<Props> = ({
	chat,
	person,
	contactProvidersData,
	isChatOnly,
	setChatTagsVisible,
	closeChat,
	waId,
}) => {
	const config = useContext(AppConfigContext);

	const dispatch = useAppDispatch();

	const { isReadOnly, hasFailedMessages } = useAppSelector((state) => state.UI);

	const isUserActionsRestricted = useIsUserActionsRestricted();

	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<Element>();

	const [isResolved, setIsResolved] = useState(false);

	useEffect(() => {
		setIsResolved(chat?.contact?.resolved ?? false);
	}, [chat]);

	const { partialUpdateChatAssignment } = useChatAssignmentAPI();

	const displayMenu = (event: MouseEvent) => {
		if (event.currentTarget instanceof Element) {
			setAnchorEl(event.currentTarget);
		}
	};

	const hideMenu = () => {
		setAnchorEl(undefined);
	};

	const showSearchMessages = () => {
		dispatch(setState({ isSearchMessagesVisible: true }));
	};

	const showContactDetails = () => {
		dispatch(setState({ isContactDetailsVisible: true }));
	};

	const showContactDetailsAndHideMenu = () => {
		showContactDetails();
		hideMenu();
	};

	const showChatAssignmentAndHideMenu = () => {
		dispatch(setState({ isChatAssignmentVisible: true }));
		hideMenu();
	};

	const showChatTagsAndHideMenu = () => {
		setChatTagsVisible(true);
		hideMenu();
	};

	const toggleAssignmentAndTaggingHistory = () => {
		setDisplayAssignmentAndTaggingHistory(
			!getDisplayAssignmentAndTaggingHistory()
		);
		hideMenu();

		// Force refresh chat
		PubSub.publish(EVENT_TOPIC_FORCE_REFRESH_CHAT, true);
	};

	const updateResolvedCall = async (resolved: boolean) => {
		hideMenu();

		try {
			const data = await updateResolved(waId, {
				resolved: resolved,
			});
			setIsResolved(data.resolved);
		} catch (error: any | AxiosError) {
			console.error(error);
		}
	};

	const closeChatAndHideMenu = () => {
		closeChat();
		hideMenu();
	};

	return (
		<Styled.Header onDrop={(event) => event.preventDefault()}>
			{!isChatOnly && (
				<Styled.MobileOnly>
					<Styled.BackButton onClick={closeChat} size="large">
						<ArrowBack />
					</Styled.BackButton>
				</Styled.MobileOnly>
			)}

			<Styled.Clickable
				onClick={person ? showContactDetails : undefined}
				$isLoading={!person}
			>
				{!person ? (
					<>
						<Skeleton variant="circular" width={40} height={40} />
						<Styled.HeaderInfo>
							<Skeleton variant="text" width={140} sx={{ fontSize: 16 }} />
							<Skeleton variant="text" width={100} sx={{ fontSize: 12 }} />
						</Styled.HeaderInfo>
					</>
				) : (
					<>
						<Styled.Avatar
							src={extractAvatarFromContactProviderData(
								contactProvidersData[waId]
							)}
							$isExpired={isPersonExpired(person)}
							generateBgColorBy={
								!isPersonExpired(person)
									? person.waba_payload?.profile?.name
									: undefined
							}
						>
							{person.initials}
						</Styled.Avatar>

						<Styled.HeaderInfo>
							<PrintMessage
								as="h3"
								message={customerDisplayName(
									contactProvidersData[waId]?.[0]?.name ??
									person.waba_payload?.profile?.name ??
									(person.wa_id && !isUserActionsRestricted
										? addPlus(person.wa_id)
										: '')
								)}
							/>

							<Styled.SubRow>
								{person.wa_id && !isUserActionsRestricted && (
									<Styled.WaId $desktopOnly>
										{addPlus(person.wa_id)}
									</Styled.WaId>
								)}

								<CustomerLanguage language={person.customer_language}/>
								{isPersonExpired(person) && (
									<Styled.ExpiredIndicator>
										{t('Expired')}
									</Styled.ExpiredIndicator>
								)}

								{isResolved && (
									<Styled.ResolvedIndicator>
										{t('Resolved')}
									</Styled.ResolvedIndicator>
								)}
							</Styled.SubRow>
						</Styled.HeaderInfo>
					</>
				)}
			</Styled.Clickable>

			<Styled.HeaderRight>
				{chat && (
					<Styled.AssigneeActions>
						{window.config?.APP_ENV_NAME !== 'cvparc-shadow' && <TagsChip waId={waId} showChatTagsList={showChatTagsAndHideMenu} />}
						<TeamLockBadge state={person?.team_state} />

						<AssigneeChip
							assigneeType="user"
							name={chat.assigned_to_user?.username}
							secondaryName={chat.assigned_group?.name}
							assignedUserId={chat.assigned_to_user?.id}
							assignedGroupId={chat.assigned_group?.id}
							isActionable={!isReadOnly}
							onAction={(userId, groupId) => {
								partialUpdateChatAssignment(waId, userId, groupId);
							}}
						/>
					</Styled.AssigneeActions>
				)}

				{isMobileOnly && hasFailedMessages && (
					<Tooltip
						title={t('Failed to send some messages!')}
						disableInteractive
					>
						<IconButton onClick={closeChat} size="large">
							<WarningIcon className="error" />
						</IconButton>
					</Tooltip>
				)}
				<IconButton onClick={showSearchMessages} size="large">
					<Search />
				</IconButton>
				<IconButton onClick={displayMenu} size="large">
					<MoreVert />
				</IconButton>
			</Styled.HeaderRight>

			<Menu
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				open={Boolean(anchorEl)}
				onClose={hideMenu}
				elevation={3}
			>
				<MenuItem onClick={showContactDetailsAndHideMenu}>
					<ListItemIcon>
						<AccountBoxIcon />
					</ListItemIcon>
					{t('Contact details')}
				</MenuItem>
				{!isReadOnly && (
					<>
						<Divider />
						{!isResolved && (
							<MenuItem onClick={() => updateResolvedCall(true)}>
								<ListItemIcon>
									<DoneIcon />
								</ListItemIcon>
								{t('Mark as resolved')}
							</MenuItem>
						)}
						{isResolved && (
							<MenuItem onClick={() => updateResolvedCall(false)}>
								<ListItemIcon>
									<SettingsBackupRestoreIcon />
								</ListItemIcon>
								{t('Mark as unresolved')}
							</MenuItem>
						)}
					</>
				)}
				{!isReadOnly && <Divider />}
				{!isReadOnly && (
					<MenuItem onClick={showChatAssignmentAndHideMenu}>
						<ListItemIcon>
							<AssignmentTurnedInIcon />
						</ListItemIcon>
						{t('Assign chat')}
					</MenuItem>
				)}
				{window.config?.APP_ENV_NAME !== 'cvparc-shadow' && !isReadOnly && (
					<MenuItem onClick={showChatTagsAndHideMenu}>
						<ListItemIcon>
							<SellIcon />
						</ListItemIcon>
						{t('Change tags')}
					</MenuItem>
				)}
				<Divider />
				<MenuItem onClick={toggleAssignmentAndTaggingHistory}>
					<ListItemIcon>
						<EventNoteIcon />
					</ListItemIcon>
					{t('Toggle event history')}
				</MenuItem>
				<Divider />
				<MenuItem onClick={closeChatAndHideMenu}>
					<ListItemIcon>
						<CloseIcon />
					</ListItemIcon>
					{t('Close view')}
				</MenuItem>
			</Menu>
		</Styled.Header>
	);
};

export default ChatHeader;
